import { useState, useEffect, FormEvent } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../services/api';
import { ArrowLeft, Save } from 'lucide-react';
import TenantVendors from '../../components/TenantVendors';

export default function TenantEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    subdomain: '',
    company_name: '',
    bc_environment_type: 'saas' as 'saas' | 'onprem',
    odata_base_url: '',
    bc_company_id: '',
    oauth_tenant_id: '',
    oauth_client_id: '',
    oauth_client_secret: '',
    oauth_scope: 'https://api.businesscentral.dynamics.com/.default',
    oauth_authority: '',
    bc_auth_type: 'oauth2' as 'oauth2' | 'basic',
    bc_username: '',
    bc_password: '',
    logo_url: '',
    primary_color: '#0a7c9f',
    is_active: true,
  });

  const [error, setError] = useState('');

  // Load existing tenant data if editing
  const { data: tenantData, isLoading } = useQuery({
    queryKey: ['tenant', id],
    queryFn: async () => {
      if (!id) return null;
      const response = await adminApi.getTenant(parseInt(id));
      return response.data;
    },
    enabled: isEditMode,
  });

  useEffect(() => {
    if (tenantData?.tenant) {
      setFormData({
        subdomain: tenantData.tenant.subdomain || '',
        company_name: tenantData.tenant.company_name || '',
        bc_environment_type: tenantData.tenant.bc_environment_type || 'saas',
        odata_base_url: tenantData.tenant.odata_base_url || '',
        bc_company_id: tenantData.tenant.bc_company_id || '',
        oauth_tenant_id: tenantData.tenant.oauth_tenant_id || '',
        oauth_client_id: tenantData.tenant.oauth_client_id || '',
        oauth_client_secret: tenantData.tenant.oauth_client_secret || '',
        oauth_scope: tenantData.tenant.oauth_scope || 'https://api.businesscentral.dynamics.com/.default',
        oauth_authority: tenantData.tenant.oauth_authority || '',
        bc_auth_type: tenantData.tenant.bc_auth_type || 'oauth2',
        bc_username: tenantData.tenant.bc_username || '',
        bc_password: tenantData.tenant.bc_password || '',
        logo_url: tenantData.tenant.logo_url || '',
        primary_color: tenantData.tenant.primary_color || '#0a7c9f',
        is_active: tenantData.tenant.is_active ?? true,
      });
    }
  }, [tenantData]);

  const saveMutation = useMutation({
    mutationFn: async (data: typeof formData) => {
      if (isEditMode && id) {
        return adminApi.updateTenant(parseInt(id), data);
      } else {
        return adminApi.createTenant(data);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['tenant', id] });
      }
      navigate('/admin/tenants');
    },
    onError: (err: any) => {
      setError(err.response?.data?.message || 'Failed to save tenant');
    },
  });

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.subdomain.match(/^[a-z0-9-]+$/)) {
      setError('Subdomain must contain only lowercase letters, numbers, and hyphens');
      return;
    }

    if (formData.subdomain === 'admin') {
      setError('Subdomain "admin" is reserved for the admin portal');
      return;
    }

    await saveMutation.mutateAsync(formData);
  };

  const handleChange = (field: string, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading tenant...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center gap-4">
            <Link to="/admin/tenants" className="p-2 hover:bg-gray-100 rounded-xl transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div>
              <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">
                {isEditMode ? 'Edit Tenant' : 'Create New Tenant'}
              </h1>
              <p className="text-sm text-gray-600">
                {isEditMode ? 'Update tenant configuration' : 'Add a new tenant to the portal'}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Form */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <form onSubmit={handleSubmit} className="card">
          <div className="p-6 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-xl">
                {error}
              </div>
            )}

            {/* Tenant code (historically the subdomain; no longer a hostname) */}
            <div>
              <label htmlFor="subdomain" className="block text-sm font-medium text-gray-700 mb-2">
                Tenant code <span className="text-red-500">*</span>
              </label>
              <div className="flex items-center gap-2">
                <input
                  id="subdomain"
                  type="text"
                  value={formData.subdomain}
                  onChange={(e) => handleChange('subdomain', e.target.value.toLowerCase())}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="bdx"
                  required
                />
              </div>
              <p className="mt-1 text-sm text-gray-500">
                Lowercase letters, numbers, and hyphens only
              </p>
            </div>

            {/* Company Name */}
            <div>
              <label htmlFor="company_name" className="block text-sm font-medium text-gray-700 mb-2">
                Company Name <span className="text-red-500">*</span>
              </label>
              <input
                id="company_name"
                type="text"
                value={formData.company_name}
                onChange={(e) => handleChange('company_name', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="BDX Transport AB"
                required
              />
            </div>

            {/* Business Central Configuration */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Business Central Connection</h3>

              {/* Environment Type */}
              <div className="mb-4">
                <label htmlFor="bc_environment_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Environment Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="bc_environment_type"
                  value={formData.bc_environment_type}
                  onChange={(e) => handleChange('bc_environment_type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="saas">Business Central SaaS</option>
                  <option value="onprem">Business Central On-Premise</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  Select whether this tenant connects to BC SaaS or On-Premise
                </p>
              </div>

            {/* OData Base URL */}
            <div>
              <label htmlFor="odata_base_url" className="block text-sm font-medium text-gray-700 mb-2">
                OData Base URL <span className="text-red-500">*</span>
              </label>
              <input
                id="odata_base_url"
                type="url"
                value={formData.odata_base_url}
                onChange={(e) => handleChange('odata_base_url', e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                placeholder="https://api.businesscentral.dynamics.com/v2.0/tenant-id/Production/api/flankSpeed/settled/v1.0"
                required
              />
              <p className="mt-1 text-sm text-gray-500">
                Full URL to the Business Central OData API endpoint
              </p>
            </div>

              {/* BC Company ID */}
              <div>
                <label htmlFor="bc_company_id" className="block text-sm font-medium text-gray-700 mb-2">
                  BC Company ID <span className="text-red-500">*</span>
                </label>
                <input
                  id="bc_company_id"
                  type="text"
                  value={formData.bc_company_id}
                  onChange={(e) => handleChange('bc_company_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Company GUID from Business Central
                </p>
              </div>
            </div>

            {/* Authentication Configuration */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Authentication configuration</h3>
              <p className="text-sm text-gray-600 mb-4">
                Configure how the portal authenticates with Business Central.
              </p>

              {/* Auth Type Selector */}
              <div className="mb-4">
                <label htmlFor="bc_auth_type" className="block text-sm font-medium text-gray-700 mb-2">
                  Authentication Type <span className="text-red-500">*</span>
                </label>
                <select
                  id="bc_auth_type"
                  value={formData.bc_auth_type}
                  onChange={(e) => handleChange('bc_auth_type', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                >
                  <option value="oauth2">OAuth2 (Azure AD)</option>
                  <option value="basic">Basic authentication</option>
                </select>
                <p className="mt-1 text-sm text-gray-500">
                  OAuth2 for BC SaaS, Basic Auth for BC On-Premise
                </p>
              </div>

              {/* OAuth2 Fields (conditional) */}
              {formData.bc_auth_type === 'oauth2' && (
                <>
                  <p className="text-sm text-gray-600 mb-4 italic">
                    Configure Azure AD app registration. Each tenant requires its own OAuth2 credentials.
                  </p>

                  {/* OAuth Tenant ID */}
              <div className="mb-4">
                <label htmlFor="oauth_tenant_id" className="block text-sm font-medium text-gray-700 mb-2">
                  Azure AD Tenant ID <span className="text-red-500">*</span>
                </label>
                <input
                  id="oauth_tenant_id"
                  type="text"
                  value={formData.oauth_tenant_id}
                  onChange={(e) => handleChange('oauth_tenant_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Azure AD Directory (tenant) ID from app registration
                </p>
              </div>

              {/* OAuth Client ID */}
              <div className="mb-4">
                <label htmlFor="oauth_client_id" className="block text-sm font-medium text-gray-700 mb-2">
                  OAuth Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  id="oauth_client_id"
                  type="text"
                  value={formData.oauth_client_id}
                  onChange={(e) => handleChange('oauth_client_id', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Application (client) ID from Azure AD app registration
                </p>
              </div>

              {/* OAuth Client Secret */}
              <div className="mb-4">
                <label htmlFor="oauth_client_secret" className="block text-sm font-medium text-gray-700 mb-2">
                  OAuth Client Secret <span className="text-red-500">*</span>
                </label>
                <input
                  id="oauth_client_secret"
                  type="password"
                  value={formData.oauth_client_secret}
                  onChange={(e) => handleChange('oauth_client_secret', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                  placeholder="Enter client secret value"
                  required
                />
                <p className="mt-1 text-sm text-gray-500">
                  Client secret value from Azure AD (stored encrypted)
                </p>
              </div>

              {/* OAuth Scope */}
              <div className="mb-4">
                <label htmlFor="oauth_scope" className="block text-sm font-medium text-gray-700 mb-2">
                  OAuth Scope
                </label>
                <input
                  id="oauth_scope"
                  type="text"
                  value={formData.oauth_scope}
                  onChange={(e) => handleChange('oauth_scope', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                  placeholder="https://api.businesscentral.dynamics.com/.default"
                />
                <p className="mt-1 text-sm text-gray-500">
                  OAuth scope (default is correct for BC SaaS)
                </p>
              </div>

              {/* OAuth Authority (for on-prem) */}
              {formData.bc_environment_type === 'onprem' && (
                <div>
                  <label htmlFor="oauth_authority" className="block text-sm font-medium text-gray-700 mb-2">
                    OAuth Authority URL
                  </label>
                  <input
                    id="oauth_authority"
                    type="url"
                    value={formData.oauth_authority}
                    onChange={(e) => handleChange('oauth_authority', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm"
                    placeholder="https://login.microsoftonline.com/{tenant-id}"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Custom OAuth authority URL for on-premise BC (optional)
                  </p>
                </div>
              )}
                </>
              )}

              {/* Basic Auth Fields (conditional) */}
              {formData.bc_auth_type === 'basic' && (
                <>
                  <p className="text-sm text-gray-600 mb-4 italic">
                    Configure username and password for Basic Authentication to BC On-Premise.
                  </p>

                  {/* BC Username */}
                  <div className="mb-4">
                    <label htmlFor="bc_username" className="block text-sm font-medium text-gray-700 mb-2">
                      Username <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="bc_username"
                      type="text"
                      value={formData.bc_username}
                      onChange={(e) => handleChange('bc_username', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="DOMAIN\\username or username"
                      required={formData.bc_auth_type === 'basic'}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Business Central username for authentication
                    </p>
                  </div>

                  {/* BC Password */}
                  <div className="mb-4">
                    <label htmlFor="bc_password" className="block text-sm font-medium text-gray-700 mb-2">
                      Password <span className="text-red-500">*</span>
                    </label>
                    <input
                      id="bc_password"
                      type="password"
                      value={formData.bc_password}
                      onChange={(e) => handleChange('bc_password', e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Enter password"
                      required={formData.bc_auth_type === 'basic'}
                    />
                    <p className="mt-1 text-sm text-gray-500">
                      Business Central password (stored securely)
                    </p>
                  </div>
                </>
              )}
            </div>

            {/* Branding */}
            <div className="border-t border-gray-200 pt-6">
              <h3 className="text-lg font-medium text-gray-900 mb-4">Branding (Optional)</h3>

              {/* Logo URL */}
              <div className="mb-4">
                <label htmlFor="logo_url" className="block text-sm font-medium text-gray-700 mb-2">
                  Logo URL
                </label>
                <input
                  id="logo_url"
                  type="url"
                  value={formData.logo_url}
                  onChange={(e) => handleChange('logo_url', e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="https://example.com/logo.png"
                />
              </div>

              {/* Primary Color */}
              <div>
                <label htmlFor="primary_color" className="block text-sm font-medium text-gray-700 mb-2">
                  Primary Color
                </label>
                <div className="flex items-center gap-4">
                  <input
                    id="primary_color"
                    type="color"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="w-16 h-10 border border-gray-300 rounded cursor-pointer"
                  />
                  <input
                    type="text"
                    value={formData.primary_color}
                    onChange={(e) => handleChange('primary_color', e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    placeholder="#0a7c9f"
                  />
                </div>
                <p className="mt-1 text-sm text-gray-500">
                  Used as a discreet accent in the vendor portal: a thin line at the top of the menu and the active menu icon.
                </p>
              </div>
            </div>

            {/* Active Status */}
            {isEditMode && (
              <div className="border-t border-gray-200 pt-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_active}
                    onChange={(e) => handleChange('is_active', e.target.checked)}
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <span className="text-sm font-medium text-gray-700">Active</span>
                </label>
                <p className="ml-6 text-sm text-gray-500">
                  Inactive tenants cannot access the portal
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end gap-3">
            <Link
              to="/admin/tenants"
              className="px-4 py-2 border border-gray-300 text-gray-700 font-medium rounded-xl hover:bg-gray-100 transition-colors"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saveMutation.isPending}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white font-medium rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Save className="w-4 h-4" />
              {saveMutation.isPending ? 'Saving...' : 'Save Tenant'}
            </button>
          </div>
        </form>

        {isEditMode && id && <TenantVendors tenantId={parseInt(id)} />}
      </main>
    </div>
  );
}
